import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Newspaper, BookOpen, MessageSquare, Globe2, MapPin, Phone, Mail, ChevronRight, Sparkles, Briefcase, FileText } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';

const RightSideMenu = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const items = [
    { label: 'Blogs', icon: BookOpen, to: '/insights', desc: 'Engineering insights & industry trends' },
    { label: 'News', icon: Newspaper, to: '/insights', desc: 'Latest announcements & project updates' },
    { label: 'Contact Us', icon: MessageSquare, to: '/contact', desc: 'Talk to our global engineering team' },
    { label: 'Careers', icon: Briefcase, to: '/careers', desc: 'Join our growing team' },
    { label: 'Coverage', icon: Globe2, to: '/coverage', desc: 'Explore our global presence' },
    { label: 'Get a Quote', icon: FileText, to: '/get-quote', desc: 'Request a free 24-hour quote' }
  ];

  return (
    <>
      {/* trigger button — always visible top right */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        data-testid="right-menu-trigger"
        className="relative inline-flex flex-col items-center justify-center w-10 h-10 rounded border border-gray-300 bg-gray-100 hover:bg-gray-200 hover:border-orange-400 transition-all group"
      >
        <span className="block w-5 h-[2px] bg-orange-500 my-[2px] transition-all group-hover:w-6"></span>
        <span className="block w-3 h-[2px] bg-green-500 my-[2px] transition-all group-hover:w-6"></span>
        <span className="block w-5 h-[2px] bg-orange-500 my-[2px] transition-all group-hover:w-6"></span>
      </button>

      {typeof document !== 'undefined' && createPortal(
        <>
          {/* overlay */}
          <div
            onClick={() => setOpen(false)}
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[9990] transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            aria-hidden="true"
            data-testid="right-menu-overlay"
          />

          {/* panel */}
          <aside
            data-testid="right-menu-panel"
            className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white border-l border-gray-200 z-[9999] shadow-2xl transition-transform duration-400 ease-out ${open ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}
            aria-hidden={!open}
          >
        {/* close + header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-gray-50">
          <div>
            <div className="text-orange-500 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Explore More
            </div>
            <div className="text-[#0e1116] font-black text-xl mt-1">Quick Access</div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="w-11 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center transition-all hover:rotate-90 duration-300 shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* items */}
        <nav className="flex-1 overflow-y-auto py-3">
          {items.map(({ label, icon: Ic, to, desc }) => (
            <Link
              key={label}
              to={to}
              onClick={() => setOpen(false)}
              className="group flex items-start gap-4 px-6 py-4 hover:bg-gray-50 transition-colors border-l-2 border-transparent hover:border-orange-500"
            >
              <div className="w-11 h-11 shrink-0 rounded bg-gray-100 group-hover:bg-orange-500 text-gray-600 group-hover:text-white flex items-center justify-center transition-all">
                <Ic className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-black text-[#0e1116] uppercase tracking-wider text-sm group-hover:text-orange-600 transition-colors">{label}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
                </div>
                <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
              </div>
            </Link>
          ))}

          {/* region accent (India) */}
          <details className="group mt-2 px-6 py-3 border-t border-gray-200">
            <summary className="flex items-center justify-between cursor-pointer list-none">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 shrink-0 rounded bg-orange-500/10 text-orange-500 flex items-center justify-center">
                  <img src="https://flagcdn.com/24x18/in.png" srcSet="https://flagcdn.com/48x36/in.png 2x" width="24" height="18" alt="India" className="rounded-sm" />
                </div>
                <div>
                  <div className="font-black text-[#0e1116] uppercase tracking-wider text-sm">India Office</div>
                  <div className="text-gray-500 text-xs">Global delivery centre</div>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
            </summary>
            <div className="mt-3 ml-[60px] space-y-2 text-xs text-gray-600">
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-green-500" /> Pune, Maharashtra, India</div>
              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-green-500" /> <a href="tel:+919960250279" className="hover:text-green-600">+91 9960 250 279</a></div>
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-green-500" /> <a href="mailto:landdrafters@gmail.com" className="hover:text-green-600">landdrafters@gmail.com</a></div>
            </div>
          </details>
        </nav>

        {/* CTA footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <div className="text-orange-500 text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Need help?</div>
          <h4 className="text-[#0e1116] font-black text-lg leading-tight">Talk to an Engineer</h4>
          <p className="text-gray-500 text-xs mt-1">24/7 support across 40+ countries.</p>
          <a href="tel:+17866732523" className="mt-4 inline-flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-3 rounded uppercase text-xs tracking-wider transition-colors">
            <Phone className="w-4 h-4" /> +1 786-673-2523
          </a>
          <a href="tel:+919960250279" className="mt-2 inline-flex items-center justify-center gap-2 w-full bg-white hover:bg-gray-100 text-[#0e1116] border border-gray-300 font-bold px-4 py-3 rounded uppercase text-xs tracking-wider transition-colors">
            <Phone className="w-4 h-4" /> +91 9960 250 279
          </a>
        </div>
      </aside>
        </>,
        document.body
      )}
    </>
  );
};

export default RightSideMenu;
